import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { debounce } from 'lodash';
import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useState
} from 'react';
import { Map as KakaoMap, MapMarker, MarkerClusterer } from 'react-kakao-maps-sdk';
import { useLocationsQuery } from '../components/api/queries/useLocationsQuery';
import EventInformation from '../components/offlineLocation/EventInformation';
import EventMarkerContainer from '../components/offlineLocation/EventMarkerContainer';
import FootprintSection from '../components/offlineLocation/FootprintSection';
import MarkerProvider from '../components/offlineLocation/MarkerProvider';
import PlaceInformation from '../components/offlineLocation/PlaceInformation';
import { Location } from '../domain/models/locations/Location';
import { ReactComponent as PlaceIcon } from '../icons/place.svg';
import { ReactComponent as CloseIcon } from '../icons/xmark.svg';
import type { CoordinatesLocation } from '../types/OfflineLocation';
import { ILocationItemResponseBody } from '../types/response/ILocationItemResponseBody';
import dDayCalculate from '@pocapan/shared/utils/dDayCalculate';
import requestGeoLocation from '@pocapan/shared/utils/requestGeoLocation';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import useKakaoLoader from '@pocapan/shared/utils/useKakaoLoader';
import BoxTabs from './tabs/BoxTabs';
import PlaceEventModal from './tabs/PlaceEventModal';

const location_icon_map = '/assets/icons/offlineLocation/location_icon_map.png';

const MarkerContext = createContext(null);

const handleGeoLocation = async (
  setUserLocation: Dispatch<SetStateAction<CoordinatesLocation>>,
  setMapLocation?: Dispatch<SetStateAction<Omit<CoordinatesLocation, 'isLoading'>>>
) => {
  setUserLocation((prev) => ({
    ...prev,
    isLoading: true
  }));

  try {
    const newLocation = await requestGeoLocation();

    // 사용자 위치 업데이트
    setUserLocation((prev) => ({
      ...prev,
      center: newLocation,
      isLoading: false,
      updateKey: prev.updateKey + 1
    }));

    // 맵 위치도 함께 업데이트 (선택적)
    if (setMapLocation) {
      setMapLocation((prev) => ({
        ...prev,
        center: newLocation,
        updateKey: prev.updateKey + 1
      }));
    }
  } catch (error) {
    setUserLocation((prev) => ({
      ...prev,
      isLoading: false
    }));
    console.log(error);
  }
};

interface OfflineLocationLikePageProps {
  selectedArtist?: any;
}

const OfflineLocationLikePage: React.FC<OfflineLocationLikePageProps> = ({ selectedArtist }) => {
  const center = { lat: 37.556944, lng: 126.923917 };
  const [positions, setPositions] = useState<Location[]>([]);
  const [mapLocation, setMapLocation] = useState<Omit<CoordinatesLocation, 'isLoading'>>({
    center,
    updateKey: 0
  });
  const [userLocation, setUserLocation] = useState<CoordinatesLocation>({
    center,
    isLoading: true,
    updateKey: 0
  });
  // const [selectedLocationId, setSelectedLocationId] = useState('');  //테스트후 삭제예정
  const [zoomLevel, setZoomLevel] = useState<number>(5);
  const [selectedPinOrOption, setSelectedPinOrOption] = useState('');

  const { colorMode } = useColorMode();
  const handleHistoryBack = useHandleHistoryBack();

  const { data: locations } = useLocationsQuery({
    latitude: mapLocation.center.lat,
    longitude: mapLocation.center.lng,
    liked: true
  });

  useEffect(() => {
    if (locations) {
      setPositions(
        locations.locations.map((location: ILocationItemResponseBody) =>
          Location.fromResponse(location)
        )
      );
    }
  }, [locations]);

  useKakaoLoader();

  const handleMapUpdate = useCallback(
    debounce((map: kakao.maps.Map) => {
      const level = map.getLevel();
      const latlng = map.getCenter();
      setZoomLevel(level);
      setMapLocation((prev) => ({
        ...prev,
        center: {
          lat: latlng.getLat(),
          lng: latlng.getLng()
        }
      }));
    }, 750),
    []
  );

  const handleChangePositionState = (pos: Location) => {
    setPositions((prev) => prev.filter((position) => position.locationId !== pos.locationId));
    setPositions((prev) => [pos, ...prev]);
    setSelectedPinOrOption(pos.locationId);
  };

  const [selectedTab, setSelectedTab] = useState(0);
  const handleSelectedTab = (index: number) => {
    setSelectedTab(index);
    if (index !== selectedTab) {
      setSelectedPinOrOption(''); // 탭이 실제로 변경될 때만 초기화
    }
    setShowBottomSheet(index === 1);
  };

  const [locationToShowInModal, setLocationToShowInModal] = useState<Location | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBottomSheet, setShowBottomSheet] = useState(true);

  // 모달 닫기 핸들러 추가
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setLocationToShowInModal(null);
    setSelectedPinOrOption(''); // 선택된 장소 초기화
  };

  // selectedPinOrOption 변경 시 모달 상태 업데이트
  useEffect(() => {
    if (selectedPinOrOption) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [selectedPinOrOption]);

  return (
    <div className="relative safeAreaPaddingTop bg-background">
      {/* 헤더 */}
      <div className="flex flex-row p-[16px] h-[54px] items-center">
        <div className="w-[24px] h-[24px] cursor-pointer z-10" onClick={handleHistoryBack}>
          <CloseIcon className="w-[24px] h-[24px] fill-pure-black dark:fill-pure-white" />
        </div>
        <div className="w-full font-semibold text-center ml-[-16px]">
          {t('OfflineLocationLike.pageTitle')}
        </div>
      </div>

      {/* 탭 */}
      <div className="px-6 py-1">
        <BoxTabs
          tabLabels={[
            <div className="font-semibold">{t('OfflineLocationLike.tabToList')}</div>,
            <div className="font-semibold">{t('OfflineLocationLike.tabToMap')}</div>
          ]}
          onTabSelected={handleSelectedTab}
        />
      </div>

      {/* 지도 탭 */}
      {selectedTab === 1 && (
        <>
          {colorMode === 'dark' ? (
            <div
              className="absolute w-full"
              style={{
                height: '76px',
                zIndex: '1',
                background: 'linear-gradient(to bottom,rgba(13, 14, 18, 1),rgba(13, 14, 18, 0))'
              }}
            />
          ) : (
            <div
              className="absolute w-full"
              style={{
                height: '76px',
                zIndex: '1',
                background:
                  'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))'
              }}
            />
          )}

          <div className="relative w-full h-screen">
            <KakaoMap
              center={mapLocation.center}
              className="w-full h-screen"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 0,
                filter: colorMode === 'dark' ? 'invert(1) hue-rotate(180deg)' : ''
              }}
              level={zoomLevel}
              onDragEnd={handleMapUpdate}
              onZoomChanged={handleMapUpdate}
              key={mapLocation.updateKey}
            >
              <MarkerClusterer
                averageCenter={true}
                minLevel={6}
                styles={[
                  {
                    width: '70px',
                    height: '70px',
                    background: 'rgba(92, 61, 250, .5)',
                    borderRadius: '70px',
                    color: '#fff',
                    textAlign: 'center',
                    lineHeight: '70px'
                  }
                ]}
              >
                {!userLocation.isLoading && (
                  <MapMarker
                    position={userLocation.center}
                    image={{
                      src: location_icon_map,
                      size: {
                        width: 22,
                        height: 22
                      }
                    }}
                  />
                )}

                <MarkerProvider MarkerContext={MarkerContext}>
                  {positions.map((pos: Location) => (
                    <EventMarkerContainer
                      key={pos.locationId}
                      pos={pos}
                      selectedLocationId={selectedPinOrOption}
                      MarkerContext={MarkerContext}
                      onChangePositionState={() => handleChangePositionState(pos)}
                      forceDarkMode={colorMode === 'dark'}
                      markerColor="#FF5492" // ★ 분홍색 고정
                      shadowColor="#FF5492"
                      offsetPx={-55} //데스크탑
                      offsetPy={90} //모바일
                    />
                  ))}
                </MarkerProvider>
              </MarkerClusterer>
            </KakaoMap>
          </div>
          <div
            className="absolute z-10 top-[calc(env(safe-area-inset-top)+156px)] left-[24px]"
            onMouseDown={() => handleGeoLocation(setUserLocation, setMapLocation)}
          >
            <div
              className="flex items-center justify-center w-[36px] h-[36px] rounded-full bg-[rgba(0,0,0,0.65)]
            dark:bg-[rgba(255,255,255,0.45)]"
            >
              <PlaceIcon className="w-[18px] h-[18px] fill-pure-white" />
            </div>
          </div>

          {/* 모달 */}
          {selectedTab === 1 && isModalOpen ? (
            (() => {
              const selectedPos = positions.find((pos) => pos.locationId === selectedPinOrOption);
              return selectedPos ? (
                <PlaceEventModal
                  key={selectedPos.locationId}
                  open={isModalOpen}
                  onClose={handleCloseModal}
                  pos={selectedPos}
                />
              ) : (
                <></>
              );
            })()
          ) : (
            <></>
          )}
        </>
      )}

      {/* 리스트 탭 */}
      {selectedTab === 0 && (
        <div className="w-full h-[calc(100vh-120px)] overflow-y-auto bg-background pt-4 ">
          <div className="font-bold text-lg ml-9 mb-4 ">
            {t('OfflineLocationLike.offlineLocationLikeText')}
            <span className="ml-1 text-primary-light dark:text-primary-dark">
              {positions.length}
            </span>
          </div>
          {positions.map((pos: Location) => {
            const firstEvent = pos?.events?.[0];
            const dDay = firstEvent
              ? dDayCalculate(firstEvent.startDateString, firstEvent.endDateString)
              : '';

            return (
              <div
                key={`${pos.latitude}-${pos.longitude}`}
                className="w-[calc(100%-48px)] max-w-[calc(100%-48px)] rounded-xl mb-[12px] pb-[16px] pt-[14px]  bg-background-light dark:bg-pure-black mx-auto" // <- 'shadow-md' 제거
                style={{
                  boxShadow:
                    colorMode === 'dark'
                      ? '0px 0px 25px 0px rgba(0,0,0,0.36)'
                      : 'rgba(0, 0, 0, 0.01) 0px 0px 0px 1px, rgba(0, 0, 0, 0.08) 0px 0px 14px 0px'
                }}
              >
                <div className="px-[16px]">
                  <PlaceInformation pos={pos} />
                </div>
                <div
                  className="mt-[14px] mb-[16px] !border-separator-light-1 dark:!border-separator-dark-1"
                  style={{ border: '1px solid' }}
                />
                {/* <hr className="mt-3 mb-3 " /> */}
                <div className="flex flex-col">
                  {pos.events.map((event, index) => (
                    <div key={event.eventId}>
                      <EventInformation event={event} siteType={pos.siteType} hasPadding={true} />
                      {index < pos.events.length - 1 && (
                        <div className="my-[12px] border border-solid !border-separator-light-1 dark:!border-separator-dark-1" />
                      )}
                    </div>
                  ))}
                </div>
                {pos.externalSources.length !== 0 && (
                  <FootprintSection pos={pos} isListItem={true} />
                )}
              </div>
            );
          })}
          <div className="h-24" />
        </div>
      )}
    </div>
  );
};

export default OfflineLocationLikePage;
